import { Component, inject, Injector, ViewEncapsulation } from '@angular/core';
import { ISmsCostsData, ISmsCostsDataAreaData, ISmsCostsPeriod } from '@fi-app/sms/sms-costs/sms-costs';
import { SmsService } from '@fi-app/sms/sms.service';
import { FiRemoteStateFullDataSource } from '@fi-app/shared/fi-items-list/fi-remote-state-full-data-source';
import { ObjectTypes } from '@fi-shared/types/object-type';
import { FiItemsListType } from '@fi-app/shared/fi-items-list/fi-items-list-type';
import { ILoadDataArgs } from '@fi-app/shared/fi-items-list/fi-data-source-base.directive';
import { IItemsListDataFetchResult } from '@fi-app/shared/fi-items-list/fi-items-list';

@Component({
    selector: 'fi-sms-costs',
    templateUrl: './sms-costs.component.html',
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./sms-costs.component.scss'],
    standalone: false,
    host: { class: 'fi-sms-costs' },
})
export class FiSmsCostsComponent extends FiRemoteStateFullDataSource<ISmsCostsDataAreaData> {
    private smsService = inject(SmsService);

    public defaultSortColumnName = '';
    public defaultSortDirection = true;
    public objectType = ObjectTypes.SMSMessage;
    public itemsListType = FiItemsListType.SMS;

    constructor(injector: Injector) {
        super(injector);
    }

    private readonly currentDate = new Date();
    protected selectedPeriod: ISmsCostsPeriod = {
        year: this.currentDate.getFullYear(),
        month: this.currentDate.getMonth(),
    };
    protected totalRow: ISmsCostsDataAreaData;

    public loadListDataFn(loadArgs: ILoadDataArgs) {
        const parameter = this.getItemsListParameter(loadArgs);
        parameter.FetchParams = {
            FetchItems: true,
            FetchTotalCount: false,
            FetchLightWeightObjects: false,
        };
        return new Promise<IItemsListDataFetchResult<ISmsCostsDataAreaData>>((resolve, reject) => {
            this.smsService.getSMSCosts(this.selectedPeriod.year, this.selectedPeriod.month + 1).then((data) => {
                this.setLoadedData(<ISmsCostsData>data);
                resolve({
                    Items: data.AreasData,
                    TotalCount: undefined,
                    LightWeightObjectsForLocalSearch: undefined,
                });
            });
        });
    }

    private setLoadedData(loadedData: ISmsCostsData): void {
        this.totalRow = {
            Id: '',
            Name: this.appTextService.get('sms.total'),
            MessagesSent: 0,
            SmsCost: 0,
        };

        let totalSmsCost = 0;
        loadedData.AreasData.forEach((areaData) => {
            areaData.ChildItems = areaData.BusinessUnitsData;
            areaData.Name = areaData.AreaName || this.appTextService.get('common.deleted_area');
            areaData.Id = areaData.AreaId ? areaData.AreaId.toString() : '-';
            areaData.MessagesSent = 0;

            let areaSmsCost = 0;

            areaData.BusinessUnitsData.forEach((businessUnitData) => {
                businessUnitData.ChildItems = businessUnitData.UsersData.slice();
                businessUnitData.Name =
                    businessUnitData.BusinessUnitName || this.appTextService.get('common.deleted_business_unit');
                businessUnitData.Id = areaData.Id + '-' + (businessUnitData.Id || '-');
                businessUnitData.MessagesSent = 0;
                let businessUnitSmsCost = 0;

                businessUnitData.UsersData.forEach((userData) => {
                    userData.Name = userData.UserFullName;
                    userData.Id = businessUnitData.Id + '-' + userData.UserId;
                    userData.SmsCost = this.getRoundedCost(userData.SmsCost);
                    businessUnitData.MessagesSent += userData.MessagesSent;
                    businessUnitSmsCost += userData.SmsCost;
                });

                areaData.MessagesSent += businessUnitData.MessagesSent;

                businessUnitData.SmsCost = this.getRoundedCost(businessUnitSmsCost);
                areaSmsCost += businessUnitData.SmsCost;
            });

            areaData.SmsCost = this.getRoundedCost(areaSmsCost);

            this.totalRow.MessagesSent += areaData.MessagesSent;
            totalSmsCost += areaData.SmsCost;
        });
        this.totalRow.SmsCost = this.getRoundedCost(totalSmsCost);

        this.setItems(loadedData.AreasData);
    }

    private getRoundedCost(cost: number): number {
        return Math.round(cost * 100) / 100;
    }
}
